// ============================================================================
// JARVISTRAVEL - SERVER BUDGET FORECAST + SUMMARY (JAR-2935)
// The app's one client for core's budget intelligence: GET /budget/summary and
// GET /budget/forecast (JAR-138/JAR-140's engine). BudgetForecast,
// BudgetManager and tripAlerts render from these answers on a core-backed trip
// instead of computing projections on the device (utils/budgetEngine.ts — the
// front-end stand-in those surfaces were built on).
//
// The mapper (summaryFromWire / forecastsFromWire) is the whole contract:
// minor units → major, core's six trend states → the app's seven display
// statuses, and the category names from the trip's own stored set, which is
// what core's answer omits. The demo exception (JAR-2892, Brent) keeps the
// local engine for demo and pre-core local trips — callers gate on trip id.
// ============================================================================

import { ApiRequestError, api } from './client';
import { confidenceLabel, type BudgetStatus } from '@/app/utils/budget';
import type { CategoryForecast, BudgetSummary } from '@/app/utils/budgetEngine';
import type { JournalTrip, BudgetCategory } from '@/app/types';

/** The low-confidence boundary, shared with confidenceLabel (utils/budget.ts). */
const CONFIDENCE_LOW = 0.45;

// ── Wire shapes (core services/trip-core/internal/budgeting/models.go) ──────

interface BudgetWithSpendingWire {
  total_budget: number;
  currency: string;
  spent: number;
  remaining: number;
  utilization: number;
  alert_tripped: boolean;
  /** Omitted (0) unless core left expenses out of the total for their currency. */
  uncounted_expense_count?: number;
}

interface CategoryWire {
  category: string;
  allocated: number;
  spent: number;
  remaining: number;
  utilization: number;
  projected_minor?: number;
  confidence_score?: number;
  color: string;
  /** What the category cannot go below: max(spent, held), in minor units. */
  committed_minor?: number;
  /** Hotels only: its stays' stand-in cost, in minor units. Not committed. */
  estimate_minor?: number;
  /** How firm that stand-in is: a flat nightly "guess", or an "estimate" from
   *  a price source. Empty when there is no stand-in. */
  estimate_source?: 'guess' | 'estimate' | '';
}

interface BudgetForecastWire {
  id: string;
  budget_id: string;
  /** Per-category answers name the category; the trip rollup's is absent. */
  category?: string;
  projection: number;
  trend_status: string;
  projected_exhaustion_at?: string;
  window: string;
  projection_days: number;
  confidence: number;
  model_version: string;
  inputs_hash: string;
}

export interface BudgetSummaryWire {
  budget: BudgetWithSpendingWire | null;
  categories: CategoryWire[] | null;
  forecast: BudgetForecastWire | null;
}

export interface CategoryForecastWire {
    /** Core lists its budget_forecasts rows (ORDER BY category NULLS FIRST): the
   *  overall row carries NO category, and per-category rows exist only after
   *  something asked for each one (service.go generates them lazily per
   *  category). Rows without a category are the trip rollup — not a card. */
  forecasts: (BudgetForecastWire & { category?: string })[] | null;
}

/** Core's trend vocabulary (budgeting.TrendStatus). */
export type TrendStatusWire =
  | 'healthy_pace'
  | 'slightly_over'
  | 'over_budget'
  | 'significantly_over'
  | 'under_budget'
  | 'low_confidence';

// ── Fetchers ─────────────────────────────────────────────────────────────────

/** GET /v1/trips/{id}/budget/summary — the trip's budget, categories and the
 *  overall forecast in one answer. Read with no fabricated fallback: a failure
 *  propagates and the caller decides what to show (api failure contract). */
export async function fetchBudgetSummary(tripId: string): Promise<BudgetSummaryWire> {
  const result = await api.get<BudgetSummaryWire>(`/trips/${encodeURIComponent(tripId)}/budget/summary`);
  if (result.error) throw result.error instanceof ApiRequestError ? result.error : new Error('Empty response from server');
  if (!result.data) throw new Error('Empty response from server');
  return result.data;
}

/** GET /v1/trips/{id}/budget/forecast — one projection per category. */
export async function fetchCategoryForecasts(tripId: string): Promise<CategoryForecastWire> {
  const result = await api.get<CategoryForecastWire>(`/trips/${encodeURIComponent(tripId)}/budget/forecast`);
  if (result.error) throw result.error instanceof ApiRequestError ? result.error : new Error('Empty response from server');
  if (!result.data) throw new Error('Empty response from server');
  return result.data;
}

// ── The contract: wire → the shapes the budget surfaces render ──────────────

const toMajor = (minor: number): number => minor / 100;

/** Core's trend status → the app's display vocabulary. A status this client
 *  does not know reads as low_confidence — an unknown from the engine is a
 *  reason to distrust the number, not a guess at a tone. */
export function trendStatusFromWire(status: string): BudgetStatus {
  switch (status as TrendStatusWire) {
    case 'healthy_pace': return 'healthy_pace';
    case 'slightly_over': return 'elevated_pace';
    case 'over_budget': return 'trending_over';
    case 'significantly_over': return 'exhaustion_risk';
    // Deliberate collapse: the app's vocabulary has no under-budget state, so
    // underspending reads as the calm tone (ocr-22). A distinct status is a
    // display-vocabulary change, not a mapper decision.
    case 'under_budget': return 'healthy_pace';
    case 'low_confidence': return 'low_confidence';
    default: return 'low_confidence';
  }
}

/** The trip's own category row for a wire category code, so the answer carries
 *  the name and color the trip (and the rest of the app) already uses. */
const categoryMeta = (trip: JournalTrip, code: string): Pick<BudgetCategory, 'name' | 'color' | 'icon'> => {
  const row = (trip.budget.categories ?? []).find((c) => c.id === code);
  return { name: row?.name ?? code, color: row?.color ?? '#5C6B77', icon: row?.icon ?? code };
};

/** The wire summary onto the app's BudgetSummary DTO. Consumes core's numbers
 *  where it answers (the projection, its delta, the trend, confidence) and the
 *  trip's own row where core's summary omits the field.
 *
 *  Units (core budgeting/models.go): core's wire is deliberately MIXED.
 *  `forecast.projection` and the per-category `projected_minor` are MINOR
 *  units — those are the only fields `toMajor` converts. `total_budget`,
 *  `spent` (budget and per-category) and category `allocated` arrive in MAJOR
 *  units already: core computes them in major (P1 expenses) or divides its
 *  minor actuals by 100 server-side (P2, mergeSpending). A toMajor on those
 *  would read the budget 100x too small. */
export function summaryFromWire(trip: JournalTrip, wire: BudgetSummaryWire): BudgetSummary {
  const budget = wire.budget;
  const forecast = wire.forecast;
  const projected = forecast ? toMajor(forecast.projection) : (budget?.spent ?? trip.budget.spent);
  const total = budget?.total_budget ?? trip.budget.total;
  const spent = budget?.spent ?? trip.budget.spent;

  // The headline driver names the heaviest over-projection among the
  // categories core answered with, named from the trip's own category set.
  // A category with no projected_minor (core omits the field when there is no
  // projection yet) reads as 0 — honestly never the heaviest over-projection.
  const rows = (wire.categories ?? []).slice().sort((a, b) => (toMajor(b.projected_minor ?? 0) - b.allocated) - (toMajor(a.projected_minor ?? 0) - a.allocated));
  const heaviest = rows[0];
  const over = heaviest && toMajor(heaviest.projected_minor ?? 0) > heaviest.allocated;
  const primary_driver = over && heaviest
    ? `${categoryMeta(trip, heaviest.category).name} is trending above plan`
    : 'Spending is tracking with your plan';
  const next_best_action = over && heaviest
    ? `Look for lower-cost ${categoryMeta(trip, heaviest.category).name.toLowerCase()} options`
    : 'You’re on track. No action needed';

  return {
    status: trendStatusFromWire(forecast?.trend_status ?? ''),
    uncounted_items: budget?.uncounted_expense_count ?? 0,
    total_budget: total,
    actual_to_date: spent,
    projected_total: projected,
    projected_delta: projected - total,
    confidence: forecast?.confidence ?? 0,
    confidence_label: confidenceLabel(forecast?.confidence ?? 0),
    primary_driver,
    next_best_action,
  };
}

/** What core holds for a category beyond its spending, in major units. */
export interface CategoryCommitment {
  /** What the category cannot go below, when that is more than it has spent:
   *  a stay's cost counts here once the traveller has given it. 0 when the
   *  spending already covers it, because core's figure is max(spent, held)
   *  and money spent is on the line already. */
  committed: number;
  /** Core's stand-in for stays with no cost of their own. 0 outside Hotels. */
  estimate: number;
  /** How firm the stand-in is, when there is one. The line words a guess as
   *  rougher than an estimate. */
  estimateSource?: 'guess' | 'estimate';
}

/** Each category's committed and estimated money, by category code. A row
 *  from a core that serves neither field is left out, so "not served" is not
 *  read as "nothing held". */
export function commitmentsFromWire(categories: CategoryWire[] | null | undefined): Record<string, CategoryCommitment> {
  const held: Record<string, CategoryCommitment> = {};
  for (const c of categories ?? []) {
    if (c.committed_minor === undefined && c.estimate_minor === undefined) continue;
    // Against the same row's spending, in minor units as core compares them.
    const committedMinor = c.committed_minor ?? 0;
    const beyondSpent = committedMinor > Math.round(c.spent * 100);
    const estimate = toMajor(c.estimate_minor ?? 0);
    held[c.category] = {
      committed: beyondSpent ? toMajor(committedMinor) : 0,
      estimate,
      // Anything but a named price-source estimate reads as the weaker guess:
      // an unknown firmness is a reason to trust the number less, not more.
      ...(estimate > 0 ? { estimateSource: c.estimate_source === 'estimate' ? 'estimate' as const : 'guess' as const } : {}),
    };
  }
  return held;
}

/** The wire per-category forecasts onto the app's CategoryForecast rows,
 *  heaviest over-projection first — the device engine's established order
 *  (budgetEngine.categoryForecasts), which the trend cards render.
 *
 *  Two wire facts shape this (core budgeting/service.go):
 *  - Rows WITHOUT a `category` are core's overall (trip-rollup) forecast —
 *    `ListForecastsByBudget` answers them first (`ORDER BY category NULLS
 *    FIRST`) — not a category card. They are skipped.
 *  - Per-category rows are created LAZILY, only when something has asked
 *    `?category=X` for that one. A category with no row yet reads as no row
 *    here — never as a zeroed card. Callers fall back to the device engine
 *    when this comes back thinner than the trip's own category set
 *    (BudgetManager does).
 *
 *  Core's `window` field is a LOOKBACK label ("7d"/"14d"/"30d" of history
 *  feeding the burn rate), not an exhaustion window, so it is deliberately
 *  not mapped: "14d" beside "runs out around day N" would read as a
 *  deadline. Core emits no exhaustion window today (self-caught correcting
 *  the ocr-20 disposition, whose premise that core provides one was false).
 */
export function forecastsFromWire(trip: JournalTrip, wire: CategoryForecastWire, summaryCategories?: CategoryWire[]): CategoryForecast[] {
  const wireRows = (wire.forecasts ?? []).filter(
    (f): f is BudgetForecastWire & { category: string } => typeof f.category === 'string' && f.category.length > 0,
  );
  const rows = wireRows.map((f) => {
    const meta = categoryMeta(trip, f.category);
    const allocated = (trip.budget.categories ?? []).find((c) => c.id === f.category)?.allocated ?? 0;
    // Core's per-category actuals ride on the summary answer (MAJOR units,
    // see summaryFromWire); the trip's own stored row (also core's, from the
    // trip read, also major) stands in when the caller only has the forecast
    // answer.
    const spent = summaryCategories?.find((c) => c.category === f.category)?.spent
      ?? (trip.budget.categories ?? []).find((c) => c.id === f.category)?.spent
      ?? 0;
    const projected = toMajor(f.projection);
    return {
      category_code: f.category,
      display_name: meta.name,
      budget: allocated,
      actual_to_date: spent,
      scheduled_future: 0,
      forecast_variable: 0,
      projected_total: projected,
      projected_delta: projected - allocated,
      projected_exhaustion_day: f.projected_exhaustion_at ? exhaustionDayOf(trip, f.projected_exhaustion_at) : undefined,
      projected_exhaustion_window: undefined,
      trend_status: trendStatusFromWire(f.trend_status),
      primary_drivers: [driverFor(meta.name, projected, allocated, f)],
    };
  });
  return rows.slice().sort((a, b) => b.projected_delta - a.projected_delta);
}

/** Which trip day a projected exhaustion instant lands on — the same count the
 *  day cards use, so "day N" means the same row on screen.
 *
 *  Matched by DATE ONLY, in the day label's own terms: itinerary days are
 *  calendar dates, and core's instant can land on the previous UTC day for an
 *  evening in a positive offset. Day-boundary precision is fine here — the
 *  card says "around day N" — and a mislabeled neighbor day is the alternative.
 *  No match reads as undefined, and the card omits the line (ocr-10). */
function exhaustionDayOf(trip: JournalTrip, at: string): number | undefined {
  const when = new Date(at);
  if (Number.isNaN(when.getTime())) return undefined;
  const days = trip.itinerary ?? [];
  const dayDate = at.slice(0, 10);
  const idx = days.findIndex((d) => d.date === dayDate);
  if (idx >= 0) return idx + 1;
  // The instant's UTC date sat between day labels (zone boundary): the day it
  // lands in is the latest label at or before it.
  const before = days.filter((d) => d.date < dayDate).length;
  return before > 0 ? before : undefined;
}

/** One plain-language driver per row, from core's own numbers. The trend
 *  status leads: a low-confidence answer is never described confidently, even
 *  when the projection happens to point over (ocr-21). The low-confidence
 *  threshold is confidenceLabel's own boundary (utils/budget.ts), imported —
 *  a change there moves this one; no second magic number (ocr-25/round-3
 *  ocr-6). An unallocated category with projected spend has no plan to be
 *  close to (round-2 ocr-6 / round-3 ocr-5). */
function driverFor(name: string, projected: number, allocated: number, f: BudgetForecastWire): string {
  if (f.trend_status === 'low_confidence' || f.confidence < CONFIDENCE_LOW) return `${name} isn’t priced yet`;
  if (allocated > 0) {
    if (projected > allocated) return `${Math.round(((projected - allocated) / allocated) * 100)}% over plan`;
    return 'close to plan';
  }
  return projected > 0 ? 'no allocation set for this yet' : 'nothing planned here yet';
}
